<template>
  <div
    ref="root"
    class="template-modal"
    tabindex="0"
    @keydown.stop.prevent="onKeydown"
  >
    <div class="modal">
      <div class="header">
        <label class="label--medium">{{ header }}</label>
        <i
          class="bi bi-x-lg"
          @click="$emit('close')"
        />
      </div>

      <div class="content">
        <slot
          name="content"
          :step="step"
          :total-steps="totalSteps"
        />
      </div>

      <div class="footer">
        <div class="multistep-controls">
          <div class="controls__side">
            <button
              class="button-tertiary"
              :disabled="isFirst"
              type="button"
              @click="handleBack"
            >
              <i class="bi bi-arrow-left" /> Back
            </button>
          </div>

          <div
            v-if="showStepIndicator"
            class="step-indicator label--medium"
          >
            {{ step }}/{{ totalSteps }}
          </div>

          <div class="controls__side controls__side--right">
            <button
              class="button-tertiary"
              :disabled="isLast"
              type="button"
              @click="handleNext"
            >
              Next<i class="bi bi-arrow-right" />
            </button>
          </div>
        </div>

        <slot
          name="actions"
          :step="step"
          :total-steps="totalSteps"
          :is-first="isFirst"
          :is-last="isLast"
          :go-next="handleNext"
          :go-back="handleBack"
        />
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TemplateMultiStepModal',
  props: {
    header: { type: String, default: '' },
    step: { type: Number, default: 1 },
    totalSteps: { type: Number, required: true },
    showStepIndicator: { type: Boolean, default: true }
  },
  computed: {
    isFirst () { return this.step <= 1 },
    isLast () { return this.step >= this.totalSteps }
  },
  mounted () {
    if (this.$refs.root && this.$refs.root.focus) this.$refs.root.focus()
  },
  methods: {
    handleNext () {
      if (this.isLast) return
      const next = this.step + 1
      this.$emit('next', next)
      this.$emit('update:step', next)
      if (next === this.totalSteps) this.$emit('last-entered')
    },
    handleBack () {
      if (this.isFirst) return
      const prev = this.step - 1
      this.$emit('back', prev)
      this.$emit('update:step', prev)
    },
    onKeydown (e) {
      if (e.key === 'Escape') this.$emit('close')
      if (e.key === 'ArrowLeft' && !this.isFirst) this.handleBack()
      if (e.key === 'ArrowRight' && !this.isLast) this.handleNext()
    }
  }
}
</script>

<style lang="scss">
.template-modal {
  @include inset-space($space-xxlarge);
  @include stack-space($space-none);
  position: fixed;
  width: 100%;
  height: 100vh;
  align-items: center;
  justify-content: center;
  padding-top: 0;
  padding-bottom: 0;
  z-index: $z-index-modal;

  @media(max-width: $mobile-breakpoint-max-width) {
    @include inset-space($space-medium);
    padding-top: 0;
    padding-bottom: 0;
  }

  .modal {
    @include stack-space($space-none);
    border-radius: $radius-medium;
    width: 100%;
    max-width: 630px;
    height: auto;
    overflow: auto;
    background-color: $color-background-primary;
    box-shadow: 0 4px 100px rgba(0, 0, 0, 0.25);

    .header {
      @include inline-space($space-medium);
      @include inset-space($space-medium);
      label {
        flex: 1;
        color: $color-text-secondary;
      }
      i { font-size: $icon-small; }
    }

    .footer {
      @include stack-space($space-medium);
      @include inset-space($space-medium);

      .multistep-controls {
        @include inline-space($space-small);
        align-items: center;
      }

      .controls__side {
        @include inline-space($space-none);
        align-items: center;
      }

      .controls__side--right {
        margin-left: auto;
      }

      .step-indicator {
        flex: 1;
        text-align: center;
      }
    }
  }
}
</style>
